import { Card } from "@pej/kore/components";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { PRODUCT } from "@/components/layout/header";
import { ThemeToggle } from "@/components/layout/theme-toggle";
import { useOpenCreatedWorkspace } from "@/contexts/use-open-created-workspace";
import { readStoredId, useWorkspaceContext } from "@/contexts/workspace-context";
import type { SelectorGroup } from "@/contexts/workspace-derivation";
import { persistId } from "@/contexts/workspace-storage";
import { CreateWorkspaceDialog } from "./create-workspace-dialog";
import { IdentityCard } from "./identity-card";
import { OrgSection } from "./org-section";

export function WorkspaceSelectorPage() {
    const { selectorGroups, platformStaff } = useWorkspaceContext();
    const openCreatedWorkspace = useOpenCreatedWorkspace();
    const navigate = useNavigate();
    const [createIn, setCreateIn] = useState<SelectorGroup["organization"] | null>(null);
    const recentId = readStoredId();

    const onSelect = useCallback(
        (id: string) => {
            persistId(id);
            void navigate({ to: "/w/$workspaceId", params: { workspaceId: id } });
        },
        [navigate],
    );

    // Heading varies by scenario:
    //   - groups empty (no access)         → no heading; the empty card carries its own copy.
    //   - all groups have zero workspaces  → bootstrap copy (createable orgs only).
    //   - mixed / has any workspaces       → "Choose a workspace".
    const heading =
        selectorGroups.length === 0
            ? null
            : selectorGroups.every((g) => g.workspaces.length === 0)
              ? "Set up your first workspace"
              : "Choose a workspace";

    const subtitle =
        heading === "Choose a workspace"
            ? "Pick where you want to work today. You can switch any time from the sidebar."
            : heading === "Set up your first workspace"
              ? "You don't have any workspaces yet. Create one inside an organization you can manage."
              : null;

    return (
        <div className="mx-auto w-full max-w-[760px] px-6 py-10">
            <div className="mb-5 flex items-center justify-between">
                <h1 className="text-lg font-semibold tracking-tight">{PRODUCT.shortName}</h1>
                <ThemeToggle />
            </div>

            <IdentityCard platformStaff={platformStaff} />

            {heading && (
                <div className="mb-4">
                    <h2 className="text-lg font-semibold tracking-tight">{heading}</h2>
                    {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
                </div>
            )}

            {selectorGroups.length === 0 ? (
                <Card className="p-6 text-center text-sm text-muted-foreground">
                    You don't have access to any workspace yet. Ask an admin to add you.
                </Card>
            ) : (
                <div>
                    {selectorGroups.map((group) => (
                        <OrgSection
                            key={group.organization.id}
                            group={group}
                            recentId={recentId}
                            onSelect={onSelect}
                            onCreate={setCreateIn}
                        />
                    ))}
                </div>
            )}

            <CreateWorkspaceDialog
                organization={createIn}
                onOpenChange={(open) => {
                    if (!open) setCreateIn(null);
                }}
                onCreated={async (id) => {
                    setCreateIn(null);
                    await openCreatedWorkspace(id);
                }}
            />
        </div>
    );
}
